import React from 'react';
import { Button } from 'react-bootstrap';
import { difference, flatMap, flatten, union, pick } from 'lodash';
import { buildUrl } from './json-api/JsonApiUtils';
import { kebabCase } from 'lodash';
import i18n from './i18n/translate';
import moment from 'moment-timezone';

/**
 * Formats string, array entries, or object keys whose value evaluates to true
 * into a single className
 * For example ["  one  two ", undefined, null, { three: false, four: true }]
 * return  "one two four"
 *
 * @param {string, array, object} classNames the input to be formatted
 */
export const formatClassName = (classNames) => {
  if (classNames === undefined || classNames === null) {
    return;
  }
  let classes;
  if (Array.isArray(classNames)) {
    classes = classNames.map(className => formatClassName(className));
  } else if (typeof classNames === 'object') {
    classes = Object.entries(classNames).map(([cn, val]) => val && cn);
  } else if (typeof classNames === 'string') {
    classes = classNames.trim().split(' ');
  }
  if (classes) {
    classes = classes.filter(cn => cn).join(' ');
    if (!classes.length) {
      classes = undefined;
    }
  }
  return classes;
}

export const simpleArrayMatch = function (array1, array2) {
  return (
    array1.length === array2.length &&
    array1.every((val, index) => val === array2[index])
  );
};

export const buildButton = (func, label, bsClass, bsStyle, disabled, props) => {
  if (!func) {
    return null;
  }
  return (
    <Button {...props} onClick={func} bsClass={bsClass} bsStyle={bsStyle || "primary"} disabled={disabled || false}>
      {label}
    </Button>);
};

const getIdSet = async (endpoint, filters, page, pageSize, query, entity, sortValue, authToken, extraFields) => {
  const fields = extraFields ? `id,${extraFields}` : 'id';
  const url = buildUrl(endpoint, {
    filters,
    sorts: sortValue ? [{ value: sortValue }] : null,
    page: { number: page, size: pageSize },
    query,
    fields: { [entity]: fields }
  });

  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    ...authToken && { 'Authorization': `Bearer ${authToken}` }
  };

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include'
  });

  return response.json();
};

/**
 * Allows selecting all records for paginated results
 *
 * @param {string} endpoint The endpoint to get select all from
 * @param {object} filters Filters to include in the request
 * @param {object} query Query to include in the request
 * @param {boolean} isChecked *true* if selecting all, *false* if deselecting
 * @param {number} totalCount The total count of data
 * @param {array} selectedIds The previously selected items IDs
 * @param {string} entity Data entity (used to request only IDs)
 * @param {string} sortValue Sort field (used to make sure requests don't duplicate results)
 * @param {string} authToken Authentication token (needed for some endpoints)
 * @param {boolean} skipInactive *true* if want to filter out deactivated records
 */
export const selectAll = async (endpoint, filters, query, isChecked, totalCount, selectedIds, entity, sortValue, authToken, skipInactive = false) => {
  const pageSize = 1000;
  const numberOfRequests = Math.ceil(totalCount / pageSize);

  let functionArray = [];
  for (let count = 0; count < numberOfRequests; count++) {
    functionArray.push(getIdSet);
  }

  try {
    const values = await Promise.all(functionArray.map((func, index) => {
      const extraFields = skipInactive ? 'active' : null;
      return func(endpoint, filters, index + 1, pageSize, query, entity, sortValue, authToken, extraFields);
    }));

    const flattened = flatMap(values, f => f.data);
    const filtered = skipInactive ? flattened.filter(item => !!item.attributes && item.attributes.active) : flattened;
    const skipped = skipInactive && isChecked
      ? flattened.filter(item => !!item.attributes && !item.attributes.active).map(item => item.id)
      : [];
    const newArray = filtered.map(item => item.id);

    let selected = [];
    if (isChecked) {
      // selecting all
      selected = flatten(union([selectedIds, newArray]));
    } else {
      // deselecting all
      selected = difference(selectedIds, newArray);
    }

    return skipInactive ? { selected, skipped } : selected;
  }
  catch (err) {
    return [];
  }
};

export const convertTimeDisplay = (time) => {
  const num = parseInt(time);
  const hours = Math.floor(num / 60);
  const minutes = num % 60;
  if (hours > 0 || minutes > 0) {
    const hourString = hours > 0 ? i18n.t('{{hours}} hr', { hours }) : '';
    const minuteString = minutes > 0 ? i18n.t('{{minutes}} min', { minutes }) : '';
    if (hours > 0 && minutes > 0) {
      return `${hourString} ${minuteString}`;
    } else if (hours > 0) {
      return hourString;
    } else if (minutes > 0) {
      return minuteString;
    }
  } else {
    return i18n.t('<1 min');
  }
};

/*
  @arrayOfProps: List of props from ownProps to merge with props:
  Example in mapStateToProps:
  import { updatePropsWithRouteProps } from "reactifi/src/Util";
  props = updatePropsWithRouteProps(props, ownProps, ['jwt', 'adminifiDomain', 'languages']);
*/
export const updatePropsWithRouteProps = (props, ownProps, arrayOfProps) => {
  for (const [key, value] of Object.entries(ownProps.route)) {
    if (arrayOfProps.includes(key)) props[key] = value;
  }
  return props;
};

/**
 * Toggles fullscreen for passed DOM element
 * @param {Node} elem A DOM node
 */
export const toggleElementFullscreen = elem => {
  if (!!elem && !document.fullscreenElement && !document.mozFullScreenElement &&
    !document.webkitFullscreenElement && !document.msFullscreenElement) {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
    }
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  }
}

/**
 * @pickDataAttributes
 *
 * @param {object} props Object to filter through
 * @returns {object} Object with all properties that contain `data-`
 */

export const pickDataAttributes = (props, componentName) => {
  const dataAttributes = pick(props, Object.keys(props).filter(prop => prop.includes('data-')));

  //Assign default data attribute if passed component name
  if(!dataAttributes['data-component-type'] && componentName){
    dataAttributes['data-component-type'] = componentName;
  }

  return dataAttributes;

}

/**
 * Generate the following data attributes
 * ( data-object-id, data-object-type, data-[dynamic attribute name from optionalAttributes] )
 * Use on element: {...dataAttribsForEl(context, { someAttribueKey: someAttributeValue })}
 * @context  the object containing the id and type.
 * @optionalAttributes  Optional object of attributes
 * { attributeName: attributeValue }
 * example: { action: 'some-action' } becomes data-action="some-action"
 * works with single word, camel, or kebab
 */
export const dataAttribsForEl = (context = { id: '', type: '' }, optionalAttributes) => { 
  let dataObject = { 'data-object-id': context.id, 'data-object-type': context.type };
  if (optionalAttributes) {
    Object.entries(optionalAttributes).map(([key, value]) => {
      dataObject[`data-${kebabCase(key)}`] = kebabCase(value);
    });
  }
  return dataObject;
}


export const formatDateToLocal = (date) => {
  if(!date) return '';
  const localizedFormattedDate = moment(date).format('MM/DD/YYYY hh:mm a');
  const localThreeLetterCode = moment.tz(moment.tz.guess()).zoneAbbr();
  return `${localizedFormattedDate} ${localThreeLetterCode}`;
}
